// Intl only, not date-fns-tz: date-fns 2, which date-fns-tz loads, declares a global `date-fns`
// module that hides date-fns 4 from the types of an app that imports this file.

/**
 * A calendar day in a given timezone, as `yyyy-MM-dd`. Days are keys, not `Date`s, so a machine
 * in another zone cannot shift them.
 */
export type DayKey = string;

const MINUTES_IN_DAY = 24 * 60;
const DAYS_IN_WEEK = 7;

/** The viewer's own timezone, as reported by the runtime. */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();

function wallClockFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = wallClockFormatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    wallClockFormatters.set(timezone, formatter);
  }
  return formatter;
}

type WallClock = { year: number; month: number; day: number; hour: number; minute: number; second: number };

function toWallClock(instant: number, timezone: string): WallClock {
  const parts = wallClockFormatter(timezone).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/** Milliseconds the zone is ahead of UTC at the instant. */
function timezoneOffset(timezone: string, instant: number): number {
  const { year, month, day, hour, minute, second } = toWallClock(instant, timezone);
  const wholeSecond = Math.floor(instant / 1000) * 1000;
  return Date.UTC(year, month - 1, day, hour, minute, second) - wholeSecond;
}

export function toDayKey(instant: number, timezone: string): DayKey {
  const { year, month, day } = toWallClock(instant, timezone);
  return utcDateToDayKey(new Date(Date.UTC(year, month - 1, day)));
}

// Day keys are read and written as UTC dates, so day maths never sees a daylight saving change.
function dayKeyToUtcDate(day: DayKey): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, date));
}

function utcDateToDayKey(date: Date): DayKey {
  return date.toISOString().slice(0, 10);
}

/** Refuses a date that does not exist, such as "2026-02-30", which the day maths would roll into March. */
export function isDayKey(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && addDaysToKey(value, 0) === value;
}

export function addDaysToKey(day: DayKey, amount: number): DayKey {
  const date = dayKeyToUtcDate(day);
  date.setUTCDate(date.getUTCDate() + amount);
  return utcDateToDayKey(date);
}

/** 0 for Sunday, as `Date.getDay`. */
export function weekdayOfKey(day: DayKey): number {
  return dayKeyToUtcDate(day).getUTCDay();
}

/** The Monday on or before the day. */
export function weekStartKey(day: DayKey): DayKey {
  const daysSinceMonday = (weekdayOfKey(day) + 6) % DAYS_IN_WEEK;
  return addDaysToKey(day, -daysSinceMonday);
}

export function weekDayKeys(weekStart: DayKey): DayKey[] {
  return Array.from({ length: DAYS_IN_WEEK }, (_, i) => addDaysToKey(weekStart, i));
}

export function minutesIntoDay(instant: number, timezone: string): number {
  const { hour, minute } = toWallClock(instant, timezone);
  return hour * 60 + minute;
}

/** Minutes past the end of the day carry into the next day, so `24 * 60` is the next midnight. */
export function wallClockToInstant(day: DayKey, minutes: number, timezone: string): number {
  const dayOffset = Math.floor(minutes / MINUTES_IN_DAY);
  const minutesInDay = minutes - dayOffset * MINUTES_IN_DAY;
  const wallClockAsUtc = dayKeyToUtcDate(addDaysToKey(day, dayOffset)).getTime() + minutesInDay * 60_000;
  return wallClockAsUtc - offsetAtWallClock(wallClockAsUtc, timezone);
}

// At a clock change the offset after the change wins: a skipped time moves back by the change, and a
// repeated time gives the later instant. Saved schedules hold instants made by this rule.
function offsetAtWallClock(wallClockAsUtc: number, timezone: string): number {
  const dayInMs = MINUTES_IN_DAY * 60_000;
  const offsetBefore = timezoneOffset(timezone, wallClockAsUtc - dayInMs);
  const offsetAfter = timezoneOffset(timezone, wallClockAsUtc + dayInMs);
  const fits = (offset: number) => timezoneOffset(timezone, wallClockAsUtc - offset) === offset;
  if (fits(offsetAfter) || !fits(offsetBefore)) {
    return offsetAfter;
  }
  return offsetBefore;
}

// Intl gives a zone's short name only in a locale that uses it (AEST in en-AU, BST in en-GB, IST
// in en-IN). Other locales give a GMT offset, so each locale is tried in turn.
const SHORT_NAME_LOCALES = ['en-US', 'en-AU', 'en-GB', 'en-IN'];
const GMT_OFFSET_NAME = /^(GMT|UTC)[+\-−]/;

// Building a formatter costs far more than using one, so each is kept.
const shortNameFormatters = new Map<string, Intl.DateTimeFormat>();

function intlShortName(timezone: string, instant: number, locale: string): string {
  const key = `${locale}|${timezone}`;
  let formatter = shortNameFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: 'short' });
    shortNameFormatters.set(key, formatter);
  }
  return formatter.formatToParts(instant).find(part => part.type === 'timeZoneName')?.value ?? '';
}

function findShortName(timezone: string, instant: number): string | undefined {
  for (const locale of SHORT_NAME_LOCALES) {
    const name = intlShortName(timezone, instant, locale);
    if (name !== '' && !GMT_OFFSET_NAME.test(name)) {
      return name;
    }
  }
  return undefined;
}

// Built by hand, not with Intl's shortOffset: ICU versions disagree on a zero offset ("GMT" or "GMT+0").
function formatGmtOffset(timezone: string, instant: number): string {
  const offsetMinutes = timezoneOffset(timezone, instant) / 60_000;
  const absoluteMinutes = Math.abs(offsetMinutes);
  const sign = offsetMinutes < 0 ? '-' : '+';
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  return `GMT${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
}

// The offset already shows a zero-offset zone, so its name adds nothing.
const ZERO_OFFSET_NAMES = new Set(['GMT', 'UTC']);

/** "AEST" at the given instant, or "GMT+8" for a zone with no short name. */
export function formatTimezoneAbbreviation(timezone: string, instant: number): string {
  return findShortName(timezone, instant) ?? formatGmtOffset(timezone, instant);
}

/**
 * "Australia/Brisbane (AEST, GMT+10)" at the given instant, without the short name when the zone has none.
 * A zone Intl does not know gives the zone alone, so a bad stored value cannot break the page.
 */
export function formatTimezoneLabel(timezone: string, instant: number): string {
  let shortName: string | undefined;
  try {
    shortName = findShortName(timezone, instant);
  } catch (error) {
    if (error instanceof RangeError) {
      return timezone;
    }
    throw error;
  }
  const offset = formatGmtOffset(timezone, instant);
  return `${timezone} (${shortName && !ZERO_OFFSET_NAMES.has(shortName) ? `${shortName}, ${offset}` : offset})`;
}
